import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { CreateVoucherRequest, VoucherCodeCheckResponse } from '../types';

type VoucherCodeStatus = 'idle' | 'dirty' | 'checking' | 'valid' | 'invalid';

interface CheckCodeMutation {
  mutateAsync: (code: string) => Promise<VoucherCodeCheckResponse>;
  isPending: boolean;
}

interface UseVoucherCodeParams {
  form: UseFormReturn<CreateVoucherRequest>;
  mutation: CheckCodeMutation;
}

const CODE_STATUS_COLORS: Partial<Record<VoucherCodeStatus, string>> = {
  dirty: '#0000A3',
  valid: '#34C759',
  invalid: '#FF383C',
};

const buildInputStyles = (color: string) => ({
  borderColor: color,
  _focus: {
    borderColor: color,
    boxShadow: `0 0 0 1px ${color}`,
  },
});

export const useVoucherCode = ({ form, mutation }: UseVoucherCodeParams) => {
  const { register, watch, setValue, clearErrors, setError, getValues } = form;

  const [codeStatus, setCodeStatus] = useState<VoucherCodeStatus>('idle');
  const [codeMessage, setCodeMessage] = useState('');
  const [lastCheckedCode, setLastCheckedCode] = useState('');

  const codeValue = watch('code');

  const sanitizeCode = useCallback((value: string) => {
    return value.replace(/\s+/g, '').toUpperCase();
  }, []);

  const handleCodeInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeCode(event.target.value);
      event.target.value = sanitized;
      setValue('code', sanitized, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [sanitizeCode, setValue]
  );

  const codeRegister = register('code', {
    onChange: handleCodeInputChange,
  });

  const setCodeValidated = useCallback(
    (nextValue: boolean) => {
      setValue('codeValidated', nextValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue]
  );

  const resetCodeValidatedIfNeeded = useCallback(() => {
    if (getValues('codeValidated')) {
      setCodeValidated(false);
    }
  }, [getValues, setCodeValidated]);

  const ensureCodeValidated = useCallback(() => {
    if (!getValues('codeValidated')) {
      setCodeValidated(true);
    }
  }, [getValues, setCodeValidated]);

  useEffect(() => {
    const normalized = sanitizeCode(codeValue || '');

    if (!normalized) {
      setCodeStatus('idle');
      setCodeMessage('');
      setLastCheckedCode('');
      clearErrors('code');
      resetCodeValidatedIfNeeded();
      return;
    }

    if (codeStatus === 'checking' || normalized === lastCheckedCode) {
      return;
    }

    setCodeStatus('dirty');
    setCodeMessage('');
    clearErrors('code');
    resetCodeValidatedIfNeeded();
  }, [
    codeValue,
    codeStatus,
    lastCheckedCode,
    clearErrors,
    sanitizeCode,
    resetCodeValidatedIfNeeded,
  ]);

  const handleClearCode = useCallback(() => {
    setValue('code', '', { shouldDirty: true, shouldTouch: true });
    setCodeStatus('idle');
    setCodeMessage('');
    setLastCheckedCode('');
    clearErrors('code');
    resetCodeValidatedIfNeeded();
  }, [clearErrors, resetCodeValidatedIfNeeded, setValue]);

  const handleCheckCode = useCallback(async () => {
    const normalizedCode = sanitizeCode(codeValue || '');

    if (!normalizedCode) {
      const message = 'Kode voucher wajib diisi';
      setError('code', { type: 'manual', message });
      setCodeStatus('invalid');
      setCodeMessage(message);
      resetCodeValidatedIfNeeded();
      return;
    }

    setCodeStatus('checking');
    setCodeMessage('');

    try {
      const response = await mutation.mutateAsync(normalizedCode);
      setLastCheckedCode(normalizedCode);
      setValue('code', normalizedCode, {
        shouldDirty: false,
        shouldTouch: true,
      });

      if (response.is_valid) {
        setCodeStatus('valid');
        setCodeMessage(response.message || 'Kode voucher dapat digunakan');
        clearErrors('code');
        ensureCodeValidated();
      } else {
        const message = response.message || 'Kode voucher pernah digunakan';
        setCodeStatus('invalid');
        setCodeMessage(message);
        setError('code', { type: 'manual', message });
        resetCodeValidatedIfNeeded();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Terjadi kesalahan saat mengecek kode voucher';
      setCodeStatus('invalid');
      setCodeMessage(message);
      setError('code', { type: 'manual', message });
      resetCodeValidatedIfNeeded();
    }
  }, [
    sanitizeCode,
    codeValue,
    mutation,
    clearErrors,
    setError,
    setValue,
    ensureCodeValidated,
    resetCodeValidatedIfNeeded,
  ]);

  const isCheckingCode = codeStatus === 'checking' || mutation.isPending;
  const canCheckCode = codeStatus === 'dirty';
  const trimmedCodeValue = sanitizeCode(codeValue || '');
  const showClearButton =
    (codeStatus === 'dirty' || codeStatus === 'invalid') &&
    Boolean(trimmedCodeValue);
  const showValidIcon = codeStatus === 'valid';

  const inputStatusStyles = useMemo(() => {
    const color = CODE_STATUS_COLORS[codeStatus];
    if (!color) {
      return {};
    }
    return buildInputStyles(color);
  }, [codeStatus]);

  return {
    codeRegister,
    codeStatus,
    codeMessage,
    isCheckingCode,
    canCheckCode,
    showClearButton,
    showValidIcon,
    inputStatusStyles,
    handleCheckCode,
    handleClearCode,
  };
};
